import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import GuideLead from '../components/GuideLead';
import GuideCTA from '../components/GuideCTA';

const GuideReconversionCV = () => {
    return (
        <div className="min-h-screen bg-slate-200">
            <Helmet>
                <title>CV pour reconversion professionnelle: valoriser ses compétences transférables</title>
                <meta name="description" content="Guide complet pour réussir sa reconversion: valoriser ses compétences transférables, gérer les écarts de carrière, adapter son CV par secteur et exemples concrets." />
                <link rel="canonical" href="https://www.cv-analyse.fr/guide/cv-reconversion-professionnelle" />
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  "itemListElement": [
                    {"@type": "ListItem","position": 1,"name": "Accueil","item": "https://www.cv-analyse.fr/"},
                    {"@type": "ListItem","position": 2,"name": "CV reconversion","item": "https://www.cv-analyse.fr/guide/cv-reconversion-professionnelle"}
                  ]
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "Article",
                  "headline": "CV pour reconversion professionnelle",
                  "description": "Guide complet pour réussir sa reconversion professionnelle",
                  "author": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "publisher": {"@type": "Organization", "name": "CV Analyse", "alternateName": "CV Analyzer"},
                  "dateModified": "2026-08-15",
                  "mainEntityOfPage": {"@type": "WebPage", "@id": "https://www.cv-analyse.fr/guide/cv-reconversion-professionnelle"}
                }
                `}</script>
                <script type="application/ld+json">{`
                {
                  "@context": "https://schema.org",
                  "@type": "HowTo",
                  "name": "Comment adapter son CV pour une reconversion professionnelle",
                  "description": "Valorisez vos compétences transférables et adaptez votre CV pour réussir votre reconversion dans un nouveau secteur.",
                  "step": [
                    {"@type": "HowToStep", "name": "Identifier ses compétences transférables", "text": "Listez les compétences acquises dans votre ancien secteur qui sont utiles dans le nouveau."},
                    {"@type": "HowToStep", "name": "Reformuler son expérience", "text": "Traduisez votre parcours dans le vocabulaire et les codes du nouveau secteur visé."},
                    {"@type": "HowToStep", "name": "Mettre en avant sa formation complémentaire", "text": "Valorisez les formations, certifications ou MOOCs suivis pour préparer la reconversion."},
                    {"@type": "HowToStep", "name": "Adapter la structure du CV", "text": "Optez pour un format fonctionnel ou mixte qui met en avant les compétences plutôt que la chronologie."},
                    {"@type": "HowToStep", "name": "Analyser son CV pour valider l'optimisation", "text": "Utilisez CV Analyse pour vérifier que votre CV est bien adapté à votre nouveau secteur."}
                  ]
                }
                `}</script>
            </Helmet>

            {/* Fil d'Ariane */}
            <nav aria-label="Fil d'Ariane" className="pt-16 pb-4">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <ol className="flex items-center space-x-2 text-sm text-slate-600" itemScope itemType="https://schema.org/BreadcrumbList">
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <Link to="/" itemProp="item" className="hover:text-slate-900">
                                <span itemProp="name">Accueil</span>
                            </Link>
                            <meta itemProp="position" content="1" />
                        </li>
                        <li className="text-slate-400">/</li>
                        <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
                            <span itemProp="name" className="text-slate-900">CV reconversion</span>
                            <meta itemProp="position" content="2" />
                        </li>
                    </ol>
                </div>
            </nav>

            {/* Hero Section */}
            <div className="bg-slate-900 pt-4 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-3xl mb-6">
                            <span className="text-4xl">🔄</span>
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                            CV pour reconversion professionnelle
                        </h1>
                        <p className="text-xl md:text-2xl text-white max-w-3xl mx-auto leading-relaxed font-medium">
                            Valorisez vos compétences transférables et réussissez votre changement de carrière
                        </p>
                    </div>
                </div>
            </div>

            <GuideLead slug="cv-reconversion-professionnelle" />

            {/* Table des matières */}
            <div className="card-surface mx-4 mt-4 max-w-4xl mx-auto">
                <div className="p-6">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">📋 Table des matières</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <a href="#competences" className="flex items-center p-3 rounded-xl hover:bg-orange-50 transition-colors">
                            <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3 text-orange-600 font-bold">1</span>
                            <span className="text-gray-700">Compétences transférables</span>
                        </a>
                        <a href="#ecarts" className="flex items-center p-3 rounded-xl hover:bg-orange-50 transition-colors">
                            <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3 text-orange-600 font-bold">2</span>
                            <span className="text-gray-700">Gérer les écarts de carrière</span>
                        </a>
                        <a href="#formations" className="flex items-center p-3 rounded-xl hover:bg-orange-50 transition-colors">
                            <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3 text-orange-600 font-bold">3</span>
                            <span className="text-gray-700">Valoriser formations et projets</span>
                        </a>
                        <a href="#exemples" className="flex items-center p-3 rounded-xl hover:bg-orange-50 transition-colors">
                            <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3 text-orange-600 font-bold">4</span>
                            <span className="text-gray-700">Exemples concrets</span>
                        </a>
                    </div>
                </div>
            </div>

            <article className="px-6 py-16 mx-auto max-w-5xl">

                {/* Section 1: Compétences transférables */}
                <div id="competences" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">🎯</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Compétences transférables</h2>
                        </div>
                        
                        <div className="bg-gradient-to-r from-orange-50 to-red-100 rounded-2xl p-6 mb-6">
                            <h3 className="text-xl font-bold text-orange-900 mb-3">💡 Identifiez vos atouts</h3>
                            <p className="text-gray-700">Même sans expérience directe dans le nouveau secteur, vous possédez des compétences précieuses à valoriser.</p>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-blue-900 mb-3">🧠 Compétences cognitives</h3>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Résolution de problèmes</li>
                                    <li>• Analyse et synthèse</li>
                                    <li>• Créativité</li>
                                    <li>• Esprit critique</li>
                                </ul>
                            </div>
                            
                            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-green-900 mb-3">👥 Compétences relationnelles</h3>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Communication</li>
                                    <li>• Travail en équipe</li>
                                    <li>• Leadership</li>
                                    <li>• Négociation</li>
                                </ul>
                            </div>
                            
                            <div className="bg-gradient-to-r from-purple-50 to-purple-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-purple-900 mb-3">⚙️ Compétences techniques</h3>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Outils informatiques</li>
                                    <li>• Gestion de projet</li>
                                    <li>• Analyse de données</li>
                                    <li>• Langues étrangères</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 2: Gérer les écarts de carrière */}
                <div id="ecarts" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-red-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">⏰</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Gérer les écarts de carrière</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-red-50 to-red-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-red-900 mb-3">📝 Reformulation positive</h3>
                                    <p className="text-gray-700 text-sm mb-4">Transformez les écarts en opportunités :</p>
                                    <div className="space-y-3">
                                        <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-500">
                                            <p className="text-red-700 text-sm italic">❌ "Période de chômage"</p>
                                        </div>
                                        <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                                            <p className="text-green-700 text-sm">✅ "Formation intensive en développement web"</p>
                                        </div>
                                        <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-500">
                                            <p className="text-green-700 text-sm">✅ "Projet personnel de reconversion"</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-orange-50 to-orange-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-orange-900 mb-3">🎯 Mise en valeur</h3>
                                <p className="text-gray-700 text-sm mb-4">Valorisez vos activités pendant l'écart :</p>
                                <ul className="text-sm text-gray-600 space-y-2">
                                    <li>• Formations et certifications</li>
                                    <li>• Projets personnels ou associatifs</li>
                                    <li>• Voyages et expériences</li>
                                    <li>• Reconversion active</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 3: Valoriser formations et projets */}
                <div id="formations" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">📚</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Valoriser formations et projets</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="space-y-4">
                                <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                                    <h3 className="text-lg font-bold text-green-900 mb-3">🎓 Formations pertinentes</h3>
                                    <p className="text-gray-700 text-sm mb-4">Mettez en avant vos apprentissages :</p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                        <li>• Certifications professionnelles</li>
                                        <li>• Formations en ligne (MOOC)</li>
                                        <li>• Stages et immersions</li>
                                        <li>• Projets d'application</li>
                                    </ul>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-blue-900 mb-3">🚀 Projets concrets</h3>
                                <p className="text-gray-700 text-sm mb-4">Démontrez vos compétences par l'action :</p>
                                <div className="space-y-2 text-sm">
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-blue-500">
                                        <strong>Projet e-commerce :</strong> Création d'un site web complet
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-green-500">
                                        <strong>Blog technique :</strong> 50 articles sur le développement
                                    </div>
                                    <div className="bg-white p-3 rounded-lg border-l-4 border-purple-500">
                                        <strong>Freelance :</strong> 3 clients satisfaits
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Section 4: Exemples concrets */}
                <div id="exemples" className="mb-16">
                    <div className="bg-white rounded-3xl p-8 shadow-xl border border-gray-100">
                        <div className="flex items-center mb-6">
                            <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl flex items-center justify-center mr-4">
                                <span className="text-2xl">💡</span>
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Exemples concrets par reconversion</h2>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-blue-900 mb-3">👨‍💼 Comptable → Développeur</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Compétences transférables :</strong>
                                        <p className="text-gray-600">Analyse de données, rigueur, Excel avancé</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Formation :</strong>
                                        <p className="text-gray-600">Bootcamp développement web (6 mois)</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Projet :</strong>
                                        <p className="text-gray-600">Application de gestion comptable en React</p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="bg-gradient-to-r from-green-50 to-green-100 rounded-2xl p-6">
                                <h3 className="text-lg font-bold text-green-900 mb-3">👩‍🏫 Enseignante → Marketing</h3>
                                <div className="space-y-3 text-sm">
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Compétences transférables :</strong>
                                        <p className="text-gray-600">Communication, pédagogie, créativité</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Formation :</strong>
                                        <p className="text-gray-600">Certification Google Analytics, formation SEO</p>
                                    </div>
                                    <div className="bg-white p-3 rounded-lg">
                                        <strong>Projet :</strong>
                                        <p className="text-gray-600">Blog éducatif avec 10k visiteurs/mois</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CTA Section */}
                <GuideCTA
                    title="Votre CV de reconversion convainc-il les recruteurs ?"
                    description="Notre IA détecte vos compétences transférables et optimise votre CV pour votre nouveau secteur — score et 3 conseils offerts."
                    ctaText="Analyser mon CV de reconversion"
                    secondaryText="Voir les tarifs"
                    theme="orange"
                />

                {/* Navigation entre guides */}
                <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Link to="/guide/rediger-cv-percutant" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mr-4 group-hover:bg-purple-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                            </svg>
                        </div>
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-purple-700">Guide précédent</h3>
                            <p className="text-sm text-gray-600">Rédiger un CV percutant</p>
                        </div>
                    </Link>
                    
                    <Link to="/guides" className="group flex items-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                        <div>
                            <h3 className="font-bold text-gray-900 group-hover:text-orange-700">Retour aux guides</h3>
                            <p className="text-sm text-gray-600">Voir tous nos guides CV</p>
                        </div>
                        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center ml-4 group-hover:bg-orange-100 transition-colors">
                            <svg className="w-6 h-6 text-gray-600 group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                        </div>
                    </Link>
                </div>
            </article>
        </div>
    );
};

export default GuideReconversionCV;
